import { useState } from 'react';
import { useSession } from '../../auth/SessionProvider';
import {
  deleteClient,
  saveClient,
  useClientMembers,
  useClients,
  useTasks,
  useTeam,
} from '../../lib/data';
import type { Client } from '../../lib/types';
import { CloseX, useToast } from '../../ui/kit';

const COLOURS = [
  '#6E4BF2',
  '#0E7A4C',
  '#1466D1',
  '#C4282E',
  '#08706B',
  '#8A5200',
  '#2F6FE4',
  '#5E6675',
];

export function ClientsDrawer() {
  const clients = useClients();
  const [editing, setEditing] = useState<Client | 'new' | null>(null);
  if (editing)
    return (
      <ClientForm client={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />
    );
  return (
    <>
      <div className="dhead">
        <div>
          <h2 id="dtitle">Clients</h2>
          <div className="sm">
            File tasks under a client. Share a client to let someone see its tasks.
          </div>
        </div>
        <CloseX />
      </div>
      <div className="dbody">
        <div className="panel">
          {(clients.data ?? []).map((c) => (
            <button key={c.id} className="sres" onClick={() => setEditing(c)}>
              <span className="cdot" style={{ background: c.colour }} aria-hidden />
              <div className="grow">
                <b>{c.name}</b>
              </div>
            </button>
          ))}
          {clients.data && !clients.data.length && <div className="empty">No clients yet.</div>}
        </div>
      </div>
      <div className="dfoot">
        <button className="btn primary big" onClick={() => setEditing('new')}>
          Add client
        </button>
      </div>
    </>
  );
}

function ClientForm({ client, onDone }: { client: Client | null; onDone: () => void }) {
  const { me } = useSession();
  const toast = useToast();
  const team = useTeam();
  const members = useClientMembers();
  const tasks = useTasks();
  const mine = !client || client.created_by === me?.id;
  const [name, setName] = useState(client?.name ?? '');
  const [colour, setColour] = useState(client?.colour ?? COLOURS[0]!);
  const [shared, setShared] = useState<Set<string>>(
    () =>
      new Set((members.data ?? []).filter((m) => m.client_id === client?.id).map((m) => m.user_id)),
  );
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const count = (tasks.data ?? []).filter((t) => t.client_id === client?.id && !t.done_at).length;

  async function save() {
    if (!name.trim()) return setErr('Add a name.');
    setBusy(true);
    try {
      await saveClient(client?.id ?? null, name.trim(), colour, [...shared]);
      toast(client ? 'Client saved' : 'Client added');
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  async function remove() {
    if (!client) return;
    if (!window.confirm(`Delete ${client.name}? Its ${count} open tasks stay, without a client.`))
      return;
    setBusy(true);
    try {
      await deleteClient(client.id);
      toast('Client deleted');
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <>
      <div className="dhead">
        <div>
          <h2 id="dtitle">{client ? client.name : 'New client'}</h2>
          <div className="sm">
            {mine ? 'You manage this client.' : 'Only the person who added it can change it.'}
          </div>
        </div>
        <CloseX />
      </div>
      <div className="dbody">
        <div className="panel grid2">
          <label className="field full">
            <span>Name</span>
            <input
              type="text"
              value={name}
              disabled={!mine}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <div className="field full">
            <span>Colour</span>
            <div
              style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
              role="radiogroup"
              aria-label="Colour"
            >
              {COLOURS.map((c) => (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={colour === c}
                  aria-label={c}
                  disabled={!mine}
                  className={`swatch ${colour === c ? 'on' : ''}`}
                  style={{ background: c }}
                  onClick={() => setColour(c)}
                />
              ))}
            </div>
          </div>
          {mine && (
            <fieldset className="field full" style={{ border: 0, padding: 0, margin: 0 }}>
              <legend style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 6 }}>
                Shared with (they see every task under this client)
              </legend>
              {(team.data ?? [])
                .filter((p) => p.id !== me?.id)
                .map((p) => (
                  <label key={p.id} className="check">
                    <input
                      type="checkbox"
                      checked={shared.has(p.id)}
                      onChange={(e) => {
                        const next = new Set(shared);
                        if (e.target.checked) next.add(p.id);
                        else next.delete(p.id);
                        setShared(next);
                      }}
                    />
                    {p.full_name}
                  </label>
                ))}
            </fieldset>
          )}
        </div>
      </div>
      <div className="dfoot">
        {err && (
          <p className="err" role="alert">
            {err}
          </p>
        )}
        {mine && (
          <button className="btn primary big" onClick={() => void save()} disabled={busy}>
            {client ? 'Save' : 'Add client'}
          </button>
        )}
        {client && mine && (
          <button className="btn ghost" onClick={() => void remove()} disabled={busy}>
            Delete
          </button>
        )}
        <button className="btn ghost" onClick={onDone}>
          Back
        </button>
      </div>
    </>
  );
}
